import React, { useState, useEffect, useRef } from 'react';
import SentientOrb from '../../orb/SentientOrb.tsx';
import './VisualFrame.css';

/**
 * Enhanced visual wrapper component with improved design and UX
 * Features:
 * - Collapsible with smooth animations
 * - Top border with instructions/description
 * - Collapse button in top left corner
 * - Animated orb that types out description
 * - Better styling and accessibility
 */
export function VisualFrame({
  title,
  type,
  description,
  children,
  defaultCollapsed = false
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [displayedDescription, setDisplayedDescription] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showCursor, setShowCursor] = useState(false);
  const [showOrb, setShowOrb] = useState(false);
  const [orbBouncing, setOrbBouncing] = useState(false);
  const hasTypedRef = useRef(false);

  // Typing animation effect
  useEffect(() => {
    if (!description || hasTypedRef.current) {
      setDisplayedDescription(description || '');
      setShowOrb(true);
      return;
    }

    hasTypedRef.current = true;
    setIsTyping(true);
    setShowCursor(true);
    let currentIndex = 0;
    const typingSpeed = 30; // milliseconds per character

    const typingInterval = setInterval(() => {
      if (currentIndex <= description.length) {
        setDisplayedDescription(description.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
        setShowCursor(false);

        // Show and bounce the orb after typing completes
        setTimeout(() => {
          setShowOrb(true);
          setOrbBouncing(true);

          // Remove bouncing class after animation completes
          setTimeout(() => {
            setOrbBouncing(false);
          }, 600);
        }, 300);
      }
    }, typingSpeed);

    return () => clearInterval(typingInterval);
  }, [description]);

  return (
    <div className="visual-frame">
      {/* Top border with collapse button and description */}
      <div className="visual-frame-header">
        <div className="visual-frame-header-content">
          {/* Collapse button in top left corner */}
          <button
            className="visual-frame-collapse-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-expanded={!isCollapsed}
            aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${title || type} visual`}
          >
            <svg
              className={`visual-frame-collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}
              viewBox="0 0 24 24"
            >
              <path
                className="visual-frame-collapse-icon-path"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Visual type badge and description */}
          <div className="visual-frame-info">
            <div className="visual-frame-badges">
              {/* <span className="visual-frame-type-badge">
                {type}
              </span> */}
              {title && (
                <span className="visual-frame-title">
                  {title}
                </span>
              )}
            </div>
            {description && (
              <p className={`visual-frame-description ${isTyping ? 'typing' : ''}`}>
                {displayedDescription}
                {showCursor && <span className="visual-frame-typing-cursor" />}
              </p>
            )}
          </div>

          {/* Experimental label */}
          <span className="visual-frame-experimental-label">
            [EXPERIMENTAL]
          </span>

          {/* Right side controls */}
          <div className="visual-frame-right-controls">
            {/* Orb container */}
            <div className={`visual-frame-orb-container ${showOrb ? 'visible' : ''} ${orbBouncing ? 'bouncing' : ''}`}>
              <SentientOrb
                color="#000"
                action="idle"
                scale={5}
              />
            </div>

            {/* Icon container */}
            <div className="visual-frame-icon-container">
              V
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible content area with smooth animation */}
      <div className={`visual-frame-content ${isCollapsed ? 'collapsed' : 'expanded'}`}>
        <div className="visual-frame-content-inner">
          {children}
        </div>
      </div>
    </div>
  );
}
